"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const lodash_1 = require("lodash");
const errors_1 = require("@deliverr/errors");
const commons_utils_1 = require("@deliverr/commons-utils");
const commons_logs_1 = require("@deliverr/commons-logs");
const PRODUCT_CLIENT_BATCH_LIMIT = (0, commons_utils_1.getEnvNumber)("PRODUCT_CLIENT_BATCH_LIMIT", 100);
class ProductClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {
        baseURL: `${process.env.PRODUCT_SERVICE_URL}`,
        basicAuthUser: process.env.PRODUCT_BASIC_AUTH_USER,
        basicAuthPassword: process.env.PRODUCT_BASIC_AUTH_PASSWORD,
    }, inventoryClient) {
        super(config);
        this.inventoryClient = inventoryClient;
        if (!config.baseURL) {
            commons_logs_1.log.warn("PRODUCT_SERVICE_URL environment variable is not set");
        }
        if (!config.basicAuthUser || !config.basicAuthPassword) {
            commons_logs_1.log.warn("PRODUCT_BASIC_AUTH_USER and PRODUCT_BASIC_AUTH_PASSWORD environment variable is not set");
        }
    }
    async getProduct(dsku, options) {
        return this.get("/v1/manager/product", Object.assign({ dsku }, options));
    }
    async getProductAuth0(dsku, options) {
        return this.get("/v1/manager/productAuth0", Object.assign({ dsku }, options));
    }
    async getProducts(dskus, options, useRequestBody = false) {
        if (useRequestBody) {
            return this.axios
                .request({
                url: "/v1/manager/products",
                data: Object.assign({ dskus }, options),
            })
                .then((res) => res.data);
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v1/manager/products", Object.assign({ dskus: dskuChunk }, options))))));
    }
    async getProductsBySellerId(sellerId, limit = 20, offset = 0, options) {
        return this.getBatch(`/v1/manager/products/${sellerId}`, Object.assign({ limit,
            offset }, options));
    }
    async getProductsBySellerIdAndMsku(sellerId, msku) {
        return this.getBatch(`/v1/products/sellerId/${sellerId}`, {
            msku,
        });
    }
    async getProductsBySellerIdAndProductAliasAttributes(sellerId, channelProductId, channelId, sellerListingSolutionUUID, includeProductAliases) {
        return this.get(`/v1/product/sellerId/${sellerId}`, {
            channelProductId,
            channelId,
            sellerListingSolutionUUID,
            includeProductAliases,
        });
    }
    async getFefoEnabledSkusForSeller(sellerId) {
        return this.get(`/v1/fefoEnabledSkus/${sellerId}`);
    }
    async getFefoEnabledSkusForSellerV2(sellerId) {
        return this.get(`/v2/fefo-enabled-skus/${sellerId}`);
    }
    async createProductBoxDimensionsForTesting(productBoxDimensions) {
        return this.post("/v1/manager/box-dimensions", productBoxDimensions);
    }
    async createProductCasePack(productCasePack) {
        return this.post("/v1/manager/case-pack", productCasePack);
    }
    async createProductsCasePack(productsCasePack) {
        return this.post("/v1/manager/case-packs", productsCasePack);
    }
    async updateProductCasePackByDsku(dsku, productCasePackData) {
        return this.put(`/v1/manager/case-pack/${dsku}`, productCasePackData);
    }
    async createProduct(product) {
        return this.post("/v1/product", product);
    }
    async createProducts(products) {
        return this.post("/v1/products", products);
    }
    async createProductV2(product) {
        return this.post("/v2/manager/product", product);
    }
    async createProductsV2(products) {
        return this.post("/v2/manager/products", products);
    }
    async update(product) {
        return this.put(`/v1/product/${product.dsku}`, product);
    }
    async createProductOrUpdateAlias(product) {
        return this.put("/v1/product", product);
    }
    async createProductsOrUpdateAliases(products) {
        return this.put("/v1/workflow/products", products);
    }
    async linkProductAlias(channelId, channelProductId, sellerId, dskuToLink, sellerListingSolutionUUID) {
        try {
            return await this.put("/v1/product-alias/link", {
                channelId,
                channelProductId,
                sellerId,
                sellerListingSolutionUUID,
                dskuToLink,
            });
        }
        catch (err) {
            this.handleError("linkProductAlias", err.response, {
                channelId,
                channelProductId,
                sellerId,
                dskuToLink,
            });
        }
    }
    async unlinkProductAlias(channelId, channelProductId, sellerId, sellerListingSolutionUUID) {
        try {
            return await this.put("/v1/product-alias/unlink", {
                channelId,
                channelProductId,
                sellerId,
                sellerListingSolutionUUID,
            });
        }
        catch (err) {
            this.handleError("unlinkProductAlias", err.response, {
                channelId,
                channelProductId,
                sellerId,
            });
        }
    }
    async getProductAlias(channelProductId, sellerListingSolutionUUID, sellerId, channelId) {
        return this.get("/v2/manager/product-alias", {
            channelProductId,
            sellerListingSolutionUUID,
            sellerId,
            channelId,
        });
    }
    async getProductAliasesBySlsUUID(sellerListingSolutionUUID) {
        return this.get("/v2/manager/product-aliases", {
            sellerListingSolutionUUID,
        });
    }
    async validateBarcodes(barcodes) {
        return this.post("/v1/barcode/validate", barcodes);
    }
    async isApprovedForSale(dsku) {
        try {
            await this.post(`/v1/product/validate/${dsku}`, undefined);
            return true;
        }
        catch (err) {
            if ([400, 404].includes(err.response.status)) {
                return false;
            }
            this.handleError("isApprovedForSale", err.response, { dsku });
        }
    }
    async batchIsApprovedForSale(dskus) {
        return this.get("/v1/products/validate", {
            dskus: dskus.join(","),
        });
    }
    async getShippingSpecificationBatch(dskus, useRequestBody = false) {
        if (useRequestBody) {
            return this.axios
                .request({
                url: "/v1/shipping-specification/",
                data: {
                    dskus,
                },
            })
                .then((res) => res.data);
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v1/shipping-specification/", {
            dskus: dskuChunk,
        })))));
    }
    async createCustomsInformation(customsInformation) {
        return this.post("/v1/manager/customs-information", customsInformation);
    }
    async updateCustomsInformation(dsku, customsInformationUpdate) {
        return this.put(`/v1/manager/customs-information/${dsku}`, customsInformationUpdate);
    }
    async createBarcodes(dskuBarcodesMap) {
        return this.post("/v1/barcode", dskuBarcodesMap);
    }
    async createCustomBarcodes(dskuBarcodesMap) {
        return this.post("/v2/manager/customBarcode", dskuBarcodesMap);
    }
    async createOrUpdateHazmatInformation(hazmatInformation) {
        return this.put("/v1/manager/hazmat", hazmatInformation);
    }
    async createOrUpdateShippingSpecification(shippingSpecification) {
        return this.put("/v1/shipping-specification", {
            [shippingSpecification.dsku]: shippingSpecification,
        });
    }
    async createOrUpdateProductPreparation(productPreparation) {
        return this.put("/v1/manager/product-preparation", productPreparation);
    }
    async createBundle(newBundle, fromDsku) {
        return this.post("/v2/manager/bundles", newBundle, {
            dsku: fromDsku,
        });
    }
    async getBundleInventory(bsku) {
        var _a;
        if ((0, lodash_1.isNil)(this.inventoryClient)) {
            throw new errors_1.DeliverrError({
                code: "400",
                payload: "InventoryClient not initialized",
            });
        }
        const bundleResponse = await this.getBundle(bsku);
        if ((0, lodash_1.isNil)(bundleResponse) ||
            (0, lodash_1.isNil)(bundleResponse.data) ||
            (0, lodash_1.isNil)(bundleResponse.data.bundle) ||
            (0, lodash_1.isNil)(bundleResponse.data.bundle.bundleItems)) {
            throw new errors_1.DeliverrError({
                code: "404",
                payload: "Bundle not found",
            });
        }
        const productATPs = await this.inventoryClient.getATP((0, lodash_1.map)(bundleResponse.data.bundle.bundleItems, (item) => item.dsku));
        const itemsAtps = bundleResponse.data.bundle.bundleItems.map(({ dsku, qty }) => { var _a, _b; return Math.floor(((_b = (_a = productATPs[dsku]) === null || _a === void 0 ? void 0 : _a.availableToPurchase) !== null && _b !== void 0 ? _b : 0) / qty); });
        return (_a = (0, lodash_1.min)(itemsAtps)) !== null && _a !== void 0 ? _a : 0;
    }
    async getBundleByAlias(channelBundleId, sellerListingSolutionUUID, sellerId, channelId, bundleDetailsOptions) {
        return this.get(`/v2/bundles/bundle-alias`, Object.assign({ channelBundleId,
            sellerListingSolutionUUID,
            sellerId,
            channelId }, bundleDetailsOptions));
    }
    async getBundlesBySellerId(sellerId, options) {
        return this.get(`/v2/manager/bundles/sellerId/${sellerId}`, options);
    }
    async getBundle(bsku, options) {
        return this.get(`/v2/bundles/${bsku}`, options);
    }
    async getBundlesByDsku(dsku) {
        return this.get(`/v2/bundles/dsku/${dsku}`);
    }
    async updateBundle(bsku, updates) {
        return this.put(`/v2/bundles/${bsku}`, updates);
    }
    async createBrandedPackaging(brandedPackagingData) {
        return this.post(`/v2/packaging/`, brandedPackagingData);
    }
    async getBrandedPackaging(dsku, options) {
        return this.get(`/v2/packaging/${dsku}`, options);
    }
    async getAllBrandedPackaging(dskus) {
        return this.get(`/v2/packaging/`, dskus);
    }
    async getAllBrandedPackagingBySellerId(options) {
        return this.get(`/v2/packaging/`, options);
    }
    async deleteBrandedPackaging(dsku) {
        return this.delete(`/v2/packaging/${dsku}`);
    }
    async searchBrandedPackaging(dskus) {
        return (await this.axios.request({
            method: "GET",
            url: "/v2/packaging/search/",
            data: {
                dskus,
            },
        })).data;
    }
    async getUnifiedProducts(dskus, options, useRequestBody) {
        if (useRequestBody) {
            return this.axios
                .request({
                url: "/v2/unified-products",
                data: Object.assign({ dskus }, options),
            })
                .then((res) => res.data);
        }
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v2/unified-products", Object.assign({ dskus: dskuChunk }, options))))));
    }
    async annotateProductAsKit(kitCreationRequest) {
        return this.post("/v1/manager/kit", kitCreationRequest);
    }
    async getProductCasePacks(dskus, options) {
        const dskuChunks = (0, lodash_1.chunk)(dskus, PRODUCT_CLIENT_BATCH_LIMIT);
        return (0, lodash_1.merge)({}, ...(await Promise.all(dskuChunks.map(async (dskuChunk) => this.getBatch("/v1/manager/case-packs", Object.assign({ dskus: dskuChunk }, options))))));
    }
    async createOrUpdateProductCategory(productCategory) {
        return this.put("/v1/product-taxonomy-node", productCategory);
    }
    async syncWarehouseItem(request) {
        return this.post("/v1/warehouse-item", request);
    }
    async getWarehouseItemsForSku(dsku, synced = true) {
        return this.get(`/v1/warehouse-item/${dsku}`, { synced });
    }
    async getWarehouseItem(warehouseId, dsku) {
        return this.get(`/v1/warehouse-item/${dsku}/warehouse/${warehouseId}`).catch((error) => {
            switch (error.response && error.response.status) {
                case 404:
                    return undefined;
                default:
                    throw error;
            }
        });
    }
    async reduceBdskusToSkuPairs(bdskus) {
        return this.get(`/v2/manager/products/sku-pairs`, { bdskus });
    }
    async updateProductAliasEligibility(channelId, dskus, eligibility) {
        try {
            return await this.put(`/v1/product-alias/eligibility`, { channelId, dskus, eligibility });
        }
        catch (err) {
            this.handleError("eligibility", err.response, {
                channelId,
                dskus: dskus.join(","),
                eligibility: eligibility.toString(),
            });
        }
    }
    async uploadKitInstructions(dsku, files, textInstruction) {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append("instructions", file);
        });
        if (textInstruction) {
            formData.append("textInstruction", textInstruction);
        }
        return this.post(`/v1/kit/instructions/${dsku}`, formData);
    }
    async getKitInstructions(dsku) {
        return this.get(`/v1/kit/instructions/${dsku}`);
    }
    handleError(name, errorResponse, logData) {
        if (errorResponse.status === 504) {
            commons_logs_1.log.error("NETWORK_ERR");
            throw new Error("NETWORK_ERR");
        }
        else if (errorResponse.status === 404) {
            commons_logs_1.log.error("NOT_FOUND");
            throw new Error("NOT_FOUND");
        }
        else {
            commons_logs_1.log.error(name, Object.assign(Object.assign({}, errorResponse.data), logData));
            throw errorResponse.data;
        }
    }
}
exports.ProductClient = ProductClient;
//# sourceMappingURL=ProductClient.js.map